import { createLogger } from '@/lib/logger'
import { toast } from 'sonner'

import { getDesktopDeepLinkUrl } from './app-links'
import { isSafeProviderRedirect } from './provider-redirect'
import { getDesktopDeepLinkScheme, isTauri, openExternal } from './tauri'

const logger = createLogger('lib/utils/oauth-connect')

/**
 * Marker the API folds into the OAuth `state` it mints so the callback knows the
 * flow began in the desktop app and has to be handed back to it.
 */
export const DESKTOP_OAUTH_CLIENT = 'desktop'

/**
 * Query params added to an `auth/url` / `install` / `initiate` request so the
 * API can mint a state that remembers this is a desktop flow, and which build
 * asked for it. Returns undefined in the browser, where nothing changes.
 */
export async function desktopOAuthParams(): Promise<
  { client: string; scheme: string } | undefined
> {
  if (!isTauri()) return undefined

  return {
    client: DESKTOP_OAUTH_CLIENT,
    scheme: await getDesktopDeepLinkScheme(),
  }
}

/**
 * Append the desktop markers to an API path that mints an auth URL.
 *
 * Applied in the API layer rather than at each call site so every provider
 * carries the marker without its hook having to know about desktop at all. In
 * the browser the path comes back untouched.
 */
export async function withDesktopOAuthParams(path: string): Promise<string> {
  const params = await desktopOAuthParams()
  if (!params) return path

  const [base, existing] = path.split('?')
  const query = new URLSearchParams(existing)
  query.set('client', params.client)
  query.set('scheme', params.scheme)

  return `${base}?${query.toString()}`
}

export { isSafeProviderRedirect } from './provider-redirect'

/**
 * Send the user out to a page a provider hosts — an OAuth consent screen, a
 * Stripe checkout, an MCP client hand-off.
 *
 * The desktop app must not navigate itself there: the Tauri webview is the app
 * shell, so a full-page redirect replaces the whole app with the provider's
 * page and leaves no way back. It also counts as an embedded webview, which
 * Google's OAuth policy rejects outright. Hand the URL to the real default
 * browser instead and let the callback deep link back into the app.
 *
 * The browser keeps the plain redirect it has always used. Anything that is
 * not a web page or an app hand-off (`javascript:`, `data:` …) is refused.
 */
export async function startProviderRedirect(
  url: string,
  assertCurrent?: () => void,
): Promise<void> {
  assertCurrent?.()
  if (!isSafeProviderRedirect(url)) {
    logger.error('Refused to open an unsafe redirect target')
    throw new Error('This connection returned an address Scrambl cannot open.')
  }
  if (!isTauri()) {
    window.location.href = url
    return
  }

  if (assertCurrent) await openExternal(url, assertCurrent)
  else await openExternal(url)
  assertCurrent?.()
  toast.info('Continue in your browser', {
    description: 'Scrambl will pick up where you leave off.',
  })
}

/**
 * Report a Connect button that never got as far as a consent screen.
 *
 * Minting the auth URL can fail before any redirect happens — most often
 * because the provider's credentials are not configured in this environment,
 * which the API answers with a 503 and a message saying which ones are missing.
 * Without this the mutation just settles and the button looks dead, so the only
 * way to find out was to open the console.
 */
export function reportProviderConnectFailure(
  provider: string,
): (error: unknown) => void {
  return error => {
    const detail = error instanceof Error ? error.message : undefined
    logger.error(`Could not start the ${provider} connection`, { error })
    toast.error(`Could not connect ${provider}`, { description: detail })
  }
}

interface DesktopOAuthState {
  client?: string
  scheme?: string
}

/**
 * Decode an OAuth `state` far enough to read the desktop markers.
 *
 * Providers mint state with slightly different encoders — base64 here,
 * base64url there — and the desktop OAuth window form-decodes the callback
 * query string, which turns a literal `+` in base64 back into a space. Try the
 * variants rather than assuming one.
 */
export function readDesktopOAuthState(
  state: string | null | undefined,
): DesktopOAuthState | null {
  if (!state) return null

  const candidates = [state, state.replace(/ /g, '+')]

  for (const candidate of candidates) {
    const normalized = candidate.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      '=',
    )

    try {
      const decoded = JSON.parse(atob(padded))
      if (decoded && typeof decoded === 'object') {
        return decoded as DesktopOAuthState
      }
    } catch {
      // Not this encoding; fall through to the next candidate.
    }
  }

  return null
}

/**
 * Whether a callback carrying this state should be handed back to the desktop
 * app rather than completed where it landed.
 */
export function isDesktopOAuthState(state: string | null | undefined): boolean {
  return readDesktopOAuthState(state)?.client === DESKTOP_OAUTH_CLIENT
}

/**
 * Hand an OAuth callback back to the desktop app.
 *
 * The provider redirected to the web callback page in the user's browser, which
 * has no Scrambl session and cannot exchange the code. Bounce the whole
 * callback URL — path, `code` and `state` intact — into the app's deep link
 * scheme. The app re-renders this same page from an authenticated session and
 * completes the exchange it was always going to make.
 *
 * Must run before anything that needs a session, so the browser never has to be
 * signed in.
 */
export function returnOAuthCallbackToDesktop(
  state: string | null | undefined,
): boolean {
  const desktopState = readDesktopOAuthState(state)
  if (desktopState?.client !== DESKTOP_OAUTH_CLIENT) return false

  const path = window.location.pathname + window.location.search
  const deepLink = getDesktopDeepLinkUrl(path, desktopState.scheme)

  logger.info('Returning OAuth callback to the desktop app', {
    scheme: desktopState.scheme,
  })
  window.location.href = deepLink
  return true
}
